/**
 * « État des locaux » — M23 + M24 (retour Manon, mi-parcours ; maquette validée
 * par Henrico le 24/09 : cet écran REMPLACE les onglets « Clés » et « États
 * des lieux »).
 *
 * Pour un bâtiment, au même endroit :
 *  · le PLAN RÉEL (image, ou PDF converti dans le navigateur) avec les portes
 *    (rectangles, déplaçables) et les interventions ouvertes (ronds rouges si
 *    urgentes, orange sinon) — un clic ouvre la fiche ;
 *  · « Situer une intervention » : choisir une fiche, puis cliquer sur le plan ;
 *  · les clés sorties (avec « Faire signer »), les interventions, les états
 *    des lieux (et « Nouvel état des lieux »).
 * Les écrans complets des clés et des modèles d'EDL restent atteignables par
 * un lien : on regroupe, on ne supprime rien.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Building2, KeyRound, Wrench, ClipboardCheck, Upload, MapPin } from 'lucide-react';
import { AppHeader } from '@/components/AppHeader';
import { ServiceTechniqueNav } from '@/components/ServiceTechniqueNav';
import { Card, Badge, Button } from '@/components/ui';
import { PlanSimple, type ElementPlan, type PastillePlan } from '@/components/PlanSimple';
import { FaireSignerButton } from '@/components/signature/FaireSignerButton';
import { EtatDesLieuxModal } from '@/pages/service-technique/EtatDesLieuxModal';
import { api, ApiException, type EtatDesLocauxDTO, type LieuDTO } from '@/lib/api';
import { pdfVersImage } from '@/lib/pdf-vers-image';
import { EdlTemplatesPanel } from '@/pages/service-technique/EdlTemplatesPanel';
import { EtatsDesLieuxListPanel } from '@/pages/service-technique/EtatsDesLieuxListPanel';
import { ClesBatimentsPanel } from '@/pages/service-technique/ClesBatimentsPanel';
import { DocumentsBatiment } from '@/components/locaux/DocumentsBatiment';
import { CarnetDuLocal } from '@/components/locaux/CarnetDuLocal';

const fr = (iso: string | null) => (iso ? new Date(`${iso.slice(0, 10)}T00:00:00Z`).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', timeZone: 'UTC' }) : '');

export function EtatDesLocauxPage() {
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const [lieux, setLieux] = useState<LieuDTO[]>([]);
  const lieuId = params.get('lieu') ?? '';
  // Chantier 7 (option B validée le 24/09) — sous-onglets, mémorisés dans l'adresse.
  const SOUS_ONGLETS = [['batiment', 'Vue du bâtiment'], ['carnet', 'Carnet du local'], ['cles', 'Portes et clés'], ['modeles', 'Modèles d’états des lieux']] as const;
  type Vue = typeof SOUS_ONGLETS[number][0];
  const vue: Vue = (SOUS_ONGLETS.some(([k]) => k === params.get('vue')) ? params.get('vue') : 'batiment') as Vue;
  const setVue = (v: Vue) => setParams(lieuId ? { lieu: lieuId, vue: v } : { vue: v });
  const [etat, setEtat] = useState<EtatDesLocauxDTO | null>(null);
  const [portes, setPortes] = useState<ElementPlan[]>([]);
  const [err, setErr] = useState<string | null>(null);
  const [envoiPlan, setEnvoiPlan] = useState(false);
  const [aSituer, setASituer] = useState<{ id: string; libelle: string } | null>(null);
  const [fichesOuvertes, setFichesOuvertes] = useState<Array<{ id: string; numero: string; sujet: string }>>([]);
  const [edlModal, setEdlModal] = useState(false);
  const fichierRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    api.admin.lieux.list().then((r) => {
      setLieux(r.lieux);
      if (!lieuId && r.lieux[0]) setParams({ lieu: r.lieux[0].id }, { replace: true });
    }).catch(() => setLieux([]));
    api.serviceTechnique.signalements.list({ statut: ['recu', 'planifie', 'en_cours'], estEtatDesLieux: false })
      .then((r) => setFichesOuvertes((r.items ?? []).map((x) => ({ id: x.id, numero: x.numero, sujet: x.sujet }))))
      .catch(() => setFichesOuvertes([]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const charger = useCallback(async () => {
    if (!lieuId) return;
    setErr(null);
    try {
      const e = await api.admin.lieux.etatDesLocaux(lieuId);
      setEtat(e);
      setPortes(e.portes.map((p) => ({
        id: p.id, code: p.libelle, libelle: p.emplacement ?? p.libelle, x: p.planX, y: p.planY,
        badge: p.nbCles ? `${p.nbCles - p.nbSorties}/${p.nbCles} en place` : null, alerte: p.nbSorties > 0,
      })));
    } catch (x) { setErr(x instanceof ApiException ? x.payload.error : 'Chargement impossible'); }
  }, [lieuId]);
  useEffect(() => { void charger(); }, [charger]);

  async function deposer(f: File) {
    if (!lieuId) return;
    setEnvoiPlan(true); setErr(null);
    try {
      const image = f.type === 'application/pdf' ? await pdfVersImage(f) : f;
      await api.admin.lieux.deposerPlan(lieuId, image);
      await charger();
    } catch (x) { setErr(x instanceof ApiException ? x.payload.error : x instanceof Error ? x.message : 'Envoi du plan impossible'); }
    finally { setEnvoiPlan(false); }
  }

  const pastilles: PastillePlan[] = (etat?.interventions ?? [])
    .filter((i) => i.x !== null && i.y !== null)
    .map((i) => ({ id: i.id, x: i.x!, y: i.y!, libelle: `${i.numero} — ${i.sujet}`,
      niveau: i.urgence === 'urgent' ? 'urgent' : i.statut === 'en_cours' ? 'encours' : 'neutre' }));

  return (
    <div className="min-h-screen">
      <AppHeader module="Service technique" accent="tech" />
      <ServiceTechniqueNav active="locaux" />
      <main id="main-content" className="mx-auto max-w-6xl space-y-4 px-4 py-6">
        <div className="flex flex-wrap items-center gap-3">
          <Building2 className="h-5 w-5 text-tech-fg" aria-hidden="true" />
          <h1 className="font-display text-xl font-semibold text-ink">État des locaux</h1>
          <label className="ml-auto flex items-center gap-2 text-sm">
            <span className="text-ink-muted">Bâtiment</span>
            <select className="h-10 rounded-r-md border border-border bg-surface px-2" value={lieuId}
              onChange={(e) => { setParams({ lieu: e.target.value, vue }); setASituer(null); }}>
              {lieux.map((l) => <option key={l.id} value={l.id}>{l.nom}</option>)}
            </select>
          </label>
        </div>
        {err && <p className="text-sm text-urgent" role="alert">{err}</p>}
        {lieux.length === 0 && <p className="text-sm text-ink-subtle">Aucun bâtiment : créez-en un dans Administration → Lieux.</p>}

        <div role="tablist" aria-label="État des locaux" className="flex flex-wrap gap-6 border-b border-border">
          {SOUS_ONGLETS.map(([k, l]) => (
            <button key={k} type="button" role="tab" aria-selected={vue === k} onClick={() => setVue(k)}
              className={`-mb-px h-10 border-b-2 px-1 text-sm ${vue === k ? 'border-tech font-medium text-ink' : 'border-transparent text-ink-muted hover:text-ink'}`}>
              {l}
            </button>
          ))}
        </div>

        {vue === 'carnet' && (
          <div className="space-y-4">
            {lieuId ? <CarnetDuLocal lieuId={lieuId} /> : <p className="text-sm text-ink-muted">Choisissez un bâtiment pour ouvrir son carnet.</p>}
            <details className="rounded-r-lg border border-border/60 p-3">
              <summary className="cursor-pointer text-sm text-ink">Tous les états des lieux (tous bâtiments)</summary>
              <div className="mt-3"><EtatsDesLieuxListPanel /></div>
            </details>
          </div>
        )}
        {vue === 'cles' && <ClesBatimentsPanel />}
        {vue === 'modeles' && <EdlTemplatesPanel />}

        {etat && vue === 'batiment' && (
          <div className="grid grid-cols-1 gap-4 lg:grid-cols-5">
            <Card variant="glass" padded className="lg:col-span-3">
              <div className="mb-2 flex flex-wrap items-center gap-2">
                <h2 className="text-sm font-semibold text-ink">Plan réel</h2>
                <span className="text-2xs text-ink-subtle">Portes = rectangles (glissez pour placer) · interventions = ronds</span>
                <span className="ml-auto flex gap-2">
                  <input ref={fichierRef} type="file" className="sr-only" accept="image/png,image/jpeg,image/webp,application/pdf"
                    aria-label="Téléverser le plan" onChange={(e) => { const f = e.target.files?.[0]; if (f) void deposer(f); e.target.value = ''; }} />
                  <Button size="sm" variant="glass" loading={envoiPlan} onClick={() => fichierRef.current?.click()}>
                    <Upload className="h-4 w-4" aria-hidden="true" /> {etat.lieu.planUrl ? 'Remplacer le plan' : 'Téléverser un plan'}
                  </Button>
                  {etat.lieu.planUrl && (
                    <Button size="sm" variant="ghost" onClick={() => void api.admin.lieux.retirerPlan(lieuId).then(charger)}>Retirer</Button>
                  )}
                </span>
              </div>
              <PlanSimple
                elements={portes}
                fondUrl={etat.lieu.planUrl}
                pastilles={pastilles}
                modifiable
                onDeplacer={(id, x, y) => setPortes((ps) => ps.map((p) => (p.id === id ? { ...p, x, y } : p)))}
                onClic={() => undefined}
                onClicPastille={(id) => navigate(`/service-technique/signalements/${id}`)}
                consigne={aSituer ? `Cliquez sur le plan à l'endroit de « ${aSituer.libelle} ».` : null}
                onClicPosition={aSituer ? (x, y) => {
                  void api.admin.lieux.situerIntervention(aSituer.id, { lieuId, x, y }).then(() => { setASituer(null); void charger(); });
                } : undefined}
                vide="Téléversez le plan du bâtiment (plan d'évacuation, d'architecte, photo), puis placez les portes."
              />
              <div className="mt-2 flex flex-wrap items-center gap-2 text-xs">
                <Button size="sm" variant="ghost" onClick={() => void Promise.all(portes.map((p) =>
                  api.admin.lieux.positionnerPorte(p.id, { planX: p.x, planY: p.y }))).then(charger)}>
                  Enregistrer la position des portes
                </Button>
                <MapPin className="h-4 w-4 text-ink-muted" aria-hidden="true" />
                <select aria-label="Situer une intervention sur le plan" className="h-8 rounded-r-md border border-border bg-surface px-2"
                  value={aSituer?.id ?? ''} onChange={(e) => {
                    const f = fichesOuvertes.find((x) => x.id === e.target.value);
                    setASituer(f ? { id: f.id, libelle: `${f.numero} — ${f.sujet}` } : null);
                  }}>
                  <option value="">Situer une intervention…</option>
                  {fichesOuvertes.map((f) => <option key={f.id} value={f.id}>{f.numero} — {f.sujet}</option>)}
                </select>
              </div>
            </Card>

            <div className="space-y-4 lg:col-span-2">
              <Card variant="glass" padded>
                <h2 className="mb-2 flex items-center gap-2 text-sm font-semibold text-ink">
                  <KeyRound className="h-4 w-4" aria-hidden="true" /> Clés
                  <span className="ml-auto text-2xs font-normal text-ink-subtle">{etat.portes.length} porte(s) · {etat.clesSorties.length} clé(s) sortie(s)</span>
                </h2>
                {etat.clesSorties.length === 0 ? <p className="text-xs text-ink-subtle">Toutes les clés sont en place.</p> : (
                  <ul className="space-y-1.5">
                    {etat.clesSorties.map((c) => (
                      <li key={c.pretId} className="text-xs">
                        <span className="text-ink">{c.porte} · exemplaire {c.exemplaire ?? '?'}</span>
                        <span className="text-ink-muted"> — {c.emprunteur}{c.retourPrevuLe ? `, retour le ${fr(c.retourPrevuLe)}` : ''}</span>
                        <span className="block"><FaireSignerButton documentType="remise_cles" documentId={c.pretId} signataireParDefaut={c.emprunteur} /></span>
                      </li>
                    ))}
                  </ul>
                )}
                <button type="button" className="mt-2 text-xs text-accent hover:underline" onClick={() => setVue('cles')}>
                  Portes et clés →
                </button>
              </Card>

              <Card variant="glass" padded>
                <h2 className="mb-2 flex items-center gap-2 text-sm font-semibold text-ink"><Wrench className="h-4 w-4" aria-hidden="true" /> Interventions</h2>
                {etat.interventions.length === 0 ? <p className="text-xs text-ink-subtle">Aucune intervention ouverte située ici.</p> : (
                  <ul className="space-y-1">
                    {etat.interventions.map((i) => (
                      <li key={i.id} className="flex items-center gap-2 text-xs">
                        <button type="button" className="text-left text-ink hover:underline" onClick={() => navigate(`/service-technique/signalements/${i.id}`)}>
                          {i.numero} — {i.sujet}
                        </button>
                        {i.urgence === 'urgent' && <Badge variant="urgent" size="sm">Urgent</Badge>}
                        {i.x === null && <span className="text-ink-subtle">(pas encore placée)</span>}
                      </li>
                    ))}
                  </ul>
                )}
              </Card>

              <Card variant="glass" padded>
                <h2 className="mb-2 flex items-center gap-2 text-sm font-semibold text-ink">
                  <ClipboardCheck className="h-4 w-4" aria-hidden="true" /> États des lieux
                  <Button size="sm" variant="ghost" className="ml-auto" onClick={() => setEdlModal(true)}>Nouvel état des lieux</Button>
                </h2>
                {etat.etatsDesLieux.length === 0 ? <p className="text-xs text-ink-subtle">Aucun état des lieux pour ce bâtiment.</p> : (
                  <ul className="space-y-1">
                    {etat.etatsDesLieux.map((e) => (
                      <li key={e.id} className="text-xs">
                        <button type="button" className="text-left text-ink hover:underline" onClick={() => navigate(`/service-technique/signalements/${e.id}`)}>
                          {e.numero} — {e.sujet}
                        </button>
                        <span className="text-ink-subtle"> · {fr(e.createdAt)}</span>
                      </li>
                    ))}
                  </ul>
                )}
                <button type="button" className="mt-2 text-xs text-accent hover:underline" onClick={() => setVue('modeles')}>
                  Modèles d’états des lieux →
                </button>
              </Card>
              {/* Chantier 7 (maquette 19) — photos et plan d'évacuation incendie. */}
              <DocumentsBatiment lieuId={lieuId} />
            </div>
          </div>
        )}
        {edlModal && (
          <EtatDesLieuxModal
            onClose={() => setEdlModal(false)}
            onCreated={(id) => { setEdlModal(false); navigate(`/service-technique/signalements/${id}`); }}
            onExistant={(id) => { setEdlModal(false); navigate(`/service-technique/signalements/${id}`); }}
          />
        )}
      </main>
    </div>
  );
}
